import { useRef, useState, type RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import { Typography, Spin, notification, Divider } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { journalsQueries, useJournalQuery, JournalRecordPageShell } from "@/entities/journal";
import {
  $journalApi,
  useJournalMutation,
  journalQueryOptions,
} from "@/entities/journal/fetch/fetch-client";
import { useLocalUser, useCoordinator } from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import { NOTIFICATION_MESSAGES, useUnifiedAutoResize, getUserFio } from "@/shared/utils";
import { s3AdapterApi } from "@entities/api";
import { useAppRoutes } from "@shared/lib/react-router";
import { JournalRecordInfo, JournalSigner } from "@shared/ui";
import { getSectionTwoActions } from "../../model/actions-map";
import { useBuildSignLine } from "../../model/mutate-hooks/use-build-sign-line";
import { useWithSignedString } from "../../model/utils/with-signed-string";
import {
  SectionSecondDetails,
  SectionSecondRepresentatives,
  SectionSecondActions,
} from "../journal-section-info/ui";
import { RecordHistoryModal } from "../record-history-modal/record-history-modal";
import { RejectSignModal } from "../reject-sign-modal/reject-sign-modal";
import "./journal-section-info-page.scss";

const { Title } = Typography;

type FileMeta = {
  fileNumber: number;
  fileId: string;
  fileName: string;
  fileType: string;
  fileContentLength: number;
  directory: string;
  md5Hash: string;
  createDate: string;
};

export const JournalSectionInfoPage = () => {
  const navigate = useNavigate();
  const { itemNoVersionId } = useParams<{ itemNoVersionId: string }>();
  const { mainJournalId, projectId, objectId, titleId } = useCoordinator();
  const appRoutes = useAppRoutes();

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectMode, setRejectMode] = useState<"signer" | "inputter">(
    "inputter",
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  const [signingString, setSigningString] = useState<string>("");
  const localUser = useLocalUser();

  const { data: mainJournal } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );

  const sectionTwoId = mainJournal?.mainJournal?.section2?.sectionId;
  const sectionTwoListId = mainJournal?.mainJournal?.section2?.sectionListId;

  const {
    data: currentItem,
    isFetching,
    isPending,
    error,
  } = useQuery({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-two/items/{itemNoVersionId}",
      {
        params: { path: { itemNoVersionId: itemNoVersionId || "" } },
      },
    ),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnReconnect: true,
    enabled: !!itemNoVersionId,
  });

  const { data: versions } = useJournalQuery(
    "get",
    "/api/v1/journals/section-two/{section2ListId}/items/{noVersionId}/versions",
    {
      params: {
        path: {
          section2ListId: sectionTwoListId || "",
          noVersionId: itemNoVersionId || "",
        },
      },
    },
    {
      staleTime: 0,
      refetchOnMount: "always",
      enabled: !!sectionTwoListId && !!itemNoVersionId,
    },
  );

  const { data: fileMeta } = useQuery<FileMeta[]>({
    queryKey: ["s3-file-meta", currentItem?.journalScanId],
    queryFn: async () => {
      const body = [{ fileNumber: 0, fileId: currentItem!.journalScanId }];
      const res = await s3AdapterApi.post("/files/meta", body);
      return res.data;
    },
    enabled: !!currentItem?.journalScanId,
  });

  const handleClose = () => {
    navigate(
      appRoutes.journals.section2(projectId, objectId, mainJournalId, titleId),
    );
  };

  const status = currentItem?.status || "";
  const actions = getSectionTwoActions(status);
  const canReturn = actions.includes("rollback");
  const canDelete = actions.includes("delete");
  const canSendToSign = actions.includes("sendToSign");
  const canRejectSign = actions.includes("rejectSign");
  const canVersions = actions.includes("versions");
  const canEdit = actions.includes("edit");

  const { mutateAsync: getSigningXml, isPending: isSigningPending } =
    useBuildSignLine();
  const { withSignedString } = useWithSignedString();

  const wrapperRef = useRef<HTMLDivElement>(null);
  useUnifiedAutoResize(wrapperRef as RefObject<HTMLElement>);

  const isSignerAwaiting =
    currentItem?.status === "AWAITING_SIGNATURE" &&
    currentItem?.signer?.userId === localUser?.user_id;

  const invalidateItem = () => {
    queryClient.invalidateQueries(
      journalQueryOptions(
        "get",
        "/api/v1/journals/section-two/items/{itemNoVersionId}",
        { params: { path: { itemNoVersionId: itemNoVersionId || "" } } },
      ),
    );
    if (!sectionTwoId) return;
    const listKey = journalsQueries.getSection2({ sectionTwoId }).queryKey;
    queryClient.invalidateQueries({ queryKey: [listKey[0], listKey[1]] });
  };

  const openReject = () => {
    setRejectMode("signer");
    setRejectOpen(true);
  };

  const openInputterReject = (value: boolean) => {
    if (value) {
      setRejectMode("inputter");
    }
    setRejectOpen(value);
  };

  const { mutateAsync: returnToWork } = useJournalMutation(
    "post",
    "/api/v1/journals/section-two/{sectionTwoListId}/items/{itemNoVersionId}/return",
  );
  const { mutateAsync: sendToSign } = useJournalMutation(
    "post",
    "/api/v1/journals/section-two/{sectionTwoListId}/items/{itemNoVersionId}/sendToSignItem",
  );
  const { mutateAsync: deleteRecord } = useJournalMutation(
    "delete",
    "/api/v1/journals/section-two/items/{itemNoVersionId}",
  );

  const handleReturnToWork = async () => {
    if (!sectionTwoListId || !itemNoVersionId) return;
    try {
      await returnToWork({
        params: { path: { sectionTwoListId, itemNoVersionId } },
      });
      invalidateItem();
    } catch (e) {
      notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_RETURN_TO_WORK_ERROR }); // "Не удалось вернуть запись в работу"
    }
  };

  const handleSendToSigning = async () => {
    if (!sectionTwoListId || !sectionTwoId || !itemNoVersionId) return;
    try {
      await sendToSign({
        params: { path: { sectionTwoListId, itemNoVersionId } },
      });
      invalidateItem();
    } catch (e) {
      notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_SEND_TO_SIGNING_ERROR_ALT }); // "Не удалось отправить на подписание"
    }
  };

  const handleDelete = async () => {
    if (!itemNoVersionId) return;
    try {
      await deleteRecord({ params: { path: { itemNoVersionId } } });
      invalidateItem();
      handleClose();
    } catch (e: any) {
      const status = e?.response?.status || e?.status;
      if (status === 409) {
        notification.error({
          message: NOTIFICATION_MESSAGES.ENTRY_DELETE_RELATED_DATA_ERROR, // "Есть связанные данные, удаление невозможно выполнить"
        });
      } else {
        notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_DELETE_NOT_POSSIBLE }); // "Не удалось удалить запись"
      }
    }
  };

  const handleEdit = () => {
    if (!itemNoVersionId) return;
    navigate(
      appRoutes.journals.section2Form(projectId, objectId, mainJournalId, titleId, itemNoVersionId),
    );
  };

  if (
    error &&
    (error.code === 500 || error.code === 403 || error.code === 404)
  ) {
    handleClose();
    return null;
  }
  if (!currentItem) return null;

  const signerFioText = currentItem?.signer
    ? getUserFio(currentItem.signer as any)
    : "";

  const currentVersion = versions?.section2List?.reduce((latest: any, current: any) => {
    if (!latest) return current;
    return (current.versionNumber || 0) > (latest.versionNumber || 0) ? current : latest;
  }, null);
  const currentVersionNumber = currentVersion?.versionNumber || 1;

  return (
    <JournalRecordPageShell ref={wrapperRef} onClose={handleClose}>
        <Spin spinning={isFetching || isPending}>
          <div className="journal-info-header journal-info-header--info-page">
            <Title level={3} style={{ marginBottom: 0 }}>
              Раздел № 2. Просмотр записи номер №{" "}
              {currentItem?.sequenceNumber}
            </Title>

            <JournalRecordInfo
              updateDate={currentItem?.updateDate}
              updateUserId={currentItem?.updateUserId}
              status={currentItem?.status}
              statusUpdateDate={currentVersion?.updateDate || currentItem?.updateDate}
              currentVersionId={currentItem?.currentVersionId}
              currentVersionNumber={currentVersionNumber}
              signerInfo={currentVersion?.signer || currentItem?.signer}
            />
          </div>

          <div>
            <SectionSecondDetails
              currentItem={currentItem}
              fileId={fileMeta?.[0]?.fileId ?? currentItem?.journalScanId}
              fileName={fileMeta?.[0]?.fileName}
            />

            <SectionSecondRepresentatives
              journalKeepersList={currentItem?.journalKeepersList || []}
            />

            <JournalSigner
              title="Подписант"
              signerLabel="Уполномоченный представитель лица, осуществляющего строительство"
              currentItem={currentItem}
              signerFio={signerFioText}
              isSignerAwaiting={isSignerAwaiting}
              sectionListId={sectionTwoListId}
              itemNoVersionId={itemNoVersionId || ""}
              localUser={localUser}
              signingString={signingString}
              isSigningPending={isSigningPending}
              getSigningXml={getSigningXml}
              setSigningString={setSigningString}
              withSignedString={withSignedString}
              invalidateItem={invalidateItem}
              openReject={openReject}
              canAction="section_2"
            />

            <Divider style={{ margin: "24px 0 16px" }} />
            <SectionSecondActions
              canSendToSign={canSendToSign}
              canRejectSign={canRejectSign}
              canReturn={canReturn}
              canDelete={canDelete}
              canVersions={canVersions}
              canEdit={canEdit}
              handleSendToSigning={handleSendToSigning}
              setRejectOpen={openInputterReject}
              handleReturnToWork={handleReturnToWork}
              handleDelete={handleDelete}
              setHistoryOpen={setHistoryOpen}
              handleEdit={handleEdit}
              handleClose={handleClose}
            />
            <RejectSignModal
              mode={rejectMode}
              sectionTwoListId={sectionTwoListId || ""}
              sectionTwoId={sectionTwoId || ""}
              itemNoVersionId={itemNoVersionId || ""}
              open={rejectOpen}
              onCancel={() => setRejectOpen(false)}
            />
            <RecordHistoryModal
              section2ListId={sectionTwoListId || ""}
              noVersionId={itemNoVersionId || ""}
              open={historyOpen}
              onCancel={() => setHistoryOpen(false)}
            />
          </div>
        </Spin>
    </JournalRecordPageShell>
  );
};
